import AuditTrail from '@/assets/icons/actions/audit-trail.svg'
import ViewIcon from '@/assets/icons/actions/view.svg'
import { TableHeader } from '@/components/table/TableHeader'
import { useProcessingVisualizedTable } from '@/hooks/shared/useProcessingVisualizedTable'
import type { TableColumn, TicketsData } from '@/utilities/types/table'
import type { SortableTableProps } from '@/utilities/types/table-props'
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableContainer,
  Tooltip,
  styled,
} from '@mui/material'
import { FC, useEffect, useRef, useState } from 'react'

import { PaginatedTableFooter } from '@/components/index'

export const StyledRow = styled(Box)(() => ({
  display: 'flex',
  width: '100%',
  padding: '16px',
  height: '52px',
  '&:nth-of-type(even)': {
    backgroundColor: '#f5f5f5',
  },
  '&:nth-of-type(odd)': {
    backgroundColor: 'white',
  },
}))

export const StyledCell = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  wordBreak: 'break-word',
  textAlign: 'center',
  fontSize: '12px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '10px',
  },
}))

const columns: TableColumn[] = [
  { id: 'ticketNo', label: 'Ticket Number', width: '10%', sortable: true },
  // { id: 'areaName', label: 'Area', width: '10%', sortable: true },
  { id: 'dealerName', label: 'Dealer', width: '10%', sortable: true },
  // { id: 'purpose', label: 'Purpose', width: '10%', sortable: true },
  { id: 'branch', label: 'Branch', width: '10%', sortable: false },
  {
    id: 'typeOfConcern',
    label: 'Type of Concern',
    width: '10%',
    sortable: true,
  },
  {
    id: 'methodOfRepair',
    label: 'Method of Repair',
    width: '10%',
    sortable: true,
  },
  { id: 'dateCreated', label: 'Date Created', width: '10%', sortable: true },
  { id: 'dateModified', label: 'Date Modified', width: '10%', sortable: true },
  { id: 'ticketAgeInDays', label: 'Ticket Age', width: '10%', sortable: true },
  { id: 'statusName', label: 'Status', width: '10%', sortable: true },
  { id: 'action', label: 'Action', width: '10%', align: 'center' },
]

interface ProcessedTableProps extends SortableTableProps<TicketsData> {}

export const MaintenanceTicketsProcessedTable: FC<ProcessedTableProps> = ({
  data = [],
  filterState,
  isLoading,
  error,
  onSort,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  totalElem,
  handleDisplay,
  handleDisplayAudit,
  handleSort,
}) => {
  const parentRef = useRef<HTMLDivElement>(null)
  const [showNoResults, setShowNoResults] = useState(false)

  const {
    virtualRows,
    totalSize,
    paginatedItems,
    handlePageChange,
    handleRowsPerPageChange,
    handleVirtualizedScroll,
  } = useProcessingVisualizedTable({
    data,
    filterState,
    containerRef: parentRef,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    totalElem,
  })

  useEffect(() => {
    if (data.length <= 0 && isLoading) {
      setShowNoResults(true)
    }
  }, [data.length])

  const getStatusStyle = (row: TicketsData) => {
    const { status, workFlowId, currentLayerId } = row

    const statusMap: Record<string, { label: string; color: string }> = {
      ON_HOLD: { label: 'ON HOLD', color: '#21A0B3' },
      PENDING: { label: 'FOR APPROVAL', color: '#FFB12F' },
      FOR_APPROVAL: { label: 'FOR APPROVAL', color: '#FFB12F' },
      FOR_REPLY_LETTER_QUOTATION_UPLOAD: {
        label: 'FOR REPLY LETTER UPLOAD',
        color: '#6743EE',
      },
      APPROVED: { label: 'APPROVED', color: '#379F86' },
      FOR_DEALERS_APPROVAL: { label: 'FOR DEALERS APPROVAL', color: '#6743EE' },
      FOR_REVISION: { label: 'FOR REVISION', color: '#FFB12F' },
      FOR_COC_UPLOADING: { label: 'FOR COC', color: '#6743EE' },
      PENDING_COC: { label: 'PENDING COC', color: '#6743EE' },
      CLOSED: { label: 'CLOSED', color: '#444862' },
      REJECTED: { label: 'REJECTED', color: '#E74A59' },
      FOR_CONTRACTOR_QUOTATION_UPLOADING: {
        label: 'FOR CONTRACTOR QUOTATION',
        color: '#6743EE',
      },
      FOR_PO_QUOTATION_UPLOADING: {
        label: 'FOR PO RELEASE',
        color: '#6743EE',
      },
      FOR_COC_DATE_AND_COMPLETION_DATE_INPUT: {
        label: 'FOR COC DATE COMPLETION',
        color: '#21A0B3',
      },
      FOR_SCHEDULE: {
        label: 'FOR SCHEDULE',
        color: '#6743EE',
      },
    }

    const label = statusMap[status]?.label || status || ''
    const statusName = String(row.statusName ?? '')
    const isRevision =
      status === 'FOR_REVISION' ||
      label.toLowerCase().includes('revision') ||
      statusName.toLowerCase().includes('revision')

    const words = label.split(' ')
    const shortenedLabel =
      words.length > 2 ? `${words.slice(0, 2).join(' ')}...` : label

    return {
      label: shortenedLabel,
      color: statusMap[status]?.color || 'transparent',
      fullLabel: label,
      textColor: isRevision ? '#BF092F' : 'white',
    }
  }

  const isRevisionRow = (row: TicketsData) => {
    const status = String(row.status ?? '')
    const statusName = String(row.statusName ?? '')
    return (
      status.toLowerCase().includes('revision') ||
      statusName.toLowerCase().includes('revision')
    )
  }
  if (isLoading) {
    return (
      <Box
        sx={{
          height: '70vh',
          width: '100%',
          background: 'white',
          zIndex: 1,
          display: 'fleex',
          justifyContent: 'center',
          alignItems: 'center',
          borderBottomLeftRadius: '10px',
          borderBottomRightRadius: '10px',
          marginTop: '2px',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', color: 'error.main' }}>
        Error: {error instanceof Error ? error.message : 'Unknown error'}
      </Box>
    )
  }

  return (
    <div
      style={{
        marginTop: '2px',
        boxShadow: 'none',
        width: '100%',
        zIndex: 1,
      }}
    >
      <TableContainer component={Paper}>
        <Table
          sx={{ minWidth: 650, minHeight: '500px' }}
          aria-label="simple table"
        >
          <TableHeader
            columns={columns}
            filterState={filterState}
            onSort={onSort}
            handleSort={handleSort}
          />
          <TableBody
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}
          >
            {paginatedItems?.length > 0 ? (
              paginatedItems.map((row, index) => {
                const isRevision = isRevisionRow(row)

                return (
                  <StyledRow
                    key={index}
                    sx={{ color: isRevision ? '#E74A59' : 'inherit' }}
                  >
                    {columns.map((column) => {
                    if (column.id === 'ticketNo') {
                      return (
                        <StyledCell
                          key={`${column.id}-${row.ticketNo}`}
                          style={{ width: column.width }}
                        >
                          {row.ticketNo}
                        </StyledCell>
                      )
                    }
                    // if (column.id === 'areaName') {
                    //   return (
                    //     <StyledCell
                    //       key={`${column.id}-${row.ticketNo}`}
                    //       width={column.width}
                    //     >
                    //       {row.areaName}
                    //     </StyledCell>
                    //   )
                    // }
                    if (column.id === 'statusName') {
                      const { label, color, fullLabel, textColor } =
                        getStatusStyle(row)

                      return (
                        <StyledCell
                          key={`${column.id}-${row.ticketNo}`}
                          width={column.width}
                        >
                          <Tooltip title={fullLabel} arrow>
                            <Box
                              sx={{
                                backgroundColor: color,
                                color: textColor,
                                padding: '4px 8px',
                                borderRadius: '4px',
                                textAlign: 'center',
                                width: '120px',
                                fontFamily: 'Gabarito',
                                fontWeight: 500,
                                fontSize: '12px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                cursor: 'pointer',
                              }}
                            >
                              {label}
                            </Box>
                          </Tooltip>
                        </StyledCell>
                      )
                    }
                    if (column.id === 'branch') {
                      return (
                        <StyledCell
                          key={`${column.id}-${row.ticketNo}`}
                          width={column.width}
                        >
                          {row.branchName || '-'}
                        </StyledCell>
                      )
                    }
                    if (column.id === 'dealerName') {
                      return (
                        <StyledCell
                          key={`${column.id}-${row.ticketNo}`}
                          width={column.width}
                        >
                          {row.dealerName || '-'}
                        </StyledCell>
                      )
                    }
                    if (column.id === 'methodOfRepair') {
                      return (
                        <StyledCell
                          key={`${column.id}-${row.ticketNo}`}
                          width={column.width}
                        >
                          {row.methodOfRepair.replace(/^C\/O/, 'c/o') || '-'}
                        </StyledCell>
                      )
                    }
                    if (column.id === 'dateCreated') {
                      return (
                        <StyledCell
                          width={column.width}
                          style={{ display: 'flex', flexDirection: 'column' }}
                        >
                          <span>
                            {new Date(row.dateCreated).toLocaleDateString(
                              'en-US',
                              {
                                month: 'short',
                                day: '2-digit',
                                year: 'numeric',
                              },
                            )}
                          </span>
                          <span>
                            {new Date(row.dateCreated).toLocaleTimeString(
                              'en-US',
                              {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true,
                              },
                            )}
                          </span>
                        </StyledCell>
                      )
                    }

                    if (column.id === 'dateModified') {
                      return (
                        <StyledCell
                          width={column.width}
                          style={{ display: 'flex', flexDirection: 'column' }}
                        >
                          <span>
                            {new Date(row.dateModified).toLocaleDateString(
                              'en-US',
                              {
                                month: 'short',
                                day: '2-digit',
                                year: 'numeric',
                              },
                            )}
                          </span>
                          <span>
                            {new Date(row.dateModified).toLocaleTimeString(
                              'en-US',
                              {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true,
                              },
                            )}
                          </span>
                        </StyledCell>
                      )
                    }

                    if (column.id === 'ticketAgeInDays') {
                      return (
                        <StyledCell
                          key={`${column.id}-${row.ticketNo}`}
                          width={column.width}
                        >
                          {row.ticketAgeInDays || '-'}
                        </StyledCell>
                      )
                    }
                    if (column.id === 'typeOfConcern') {
                      return (
                        <StyledCell
                          key={`${column.id}-${row.ticketNo}`}
                          width={column.width}
                        >
                          {row.typeOfConcern || '-'}
                        </StyledCell>
                      )
                    }

                    if (column.id === 'status') {
                      return (
                        <StyledCell
                          key={`${column.id}-${row.ticketNo}`}
                          width={column.width}
                        >
                          {row.statusName || '-'}
                        </StyledCell>
                      )
                    }
                    if (column.id === 'action') {
                      return (
                        <StyledCell
                          key={`${column.id}-${row.id}`}
                          width={column.width}
                        >
                          <IconButton
                            sx={{
                              padding: '4px',
                              '&:hover': { bgcolor: 'rgba(73, 90, 255, 0.05)' },
                            }}
                            onClick={() => handleDisplay?.(row)}
                          >
                            <img
                              src={ViewIcon}
                              alt="View"
                              width={16}
                              height={16}
                            />
                          </IconButton>
                          <IconButton
                            sx={{
                              padding: '4px',
                              '&:hover': { bgcolor: 'rgba(73, 90, 255, 0.05)' },
                            }}
                            onClick={() => {
                              if (handleDisplayAudit) {
                                handleDisplayAudit(row.id)
                              }
                            }}
                          >
                            <img
                              src={AuditTrail}
                              alt="Audit"
                              width={16}
                              height={16}
                            />
                          </IconButton>
                        </StyledCell>
                      )
                    }
                    return null // fallback to prevent warnings
                    })}
                  </StyledRow>
                )
              })
            ) : (
              <StyledRow>
                <StyledCell
                  sx={{
                    width: '100%',
                    justifyContent: 'center',
                    alignItems: 'center',
                    display: 'flex',
                    color: '#888',
                    fontSize: '14px',
                    fontStyle: 'italic',
                    height: '52px',
                  }}
                >
                  No tickets found.
                </StyledCell>
              </StyledRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <PaginatedTableFooter
        rowsPerPage={rowsPerPage}
        totalCount={totalElem}
        page={page}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </div>
  )
}
